/**
 * Кассын нэмэгдэл — и-баримт (шийдвэр 2026-09-27).
 *
 *  · И-баримт +10% — НӨАТ. Худалдан авагч баримт авбал нэмэгдэнэ.
 *  · Зээлийн апп орсон бол и-баримт ЗААВАЛ, ЗӨВХӨН аппын хэсэгт
 *    (2026-10-06): 1,000,000₮-ийн гутал, бэлэн 500,000 + апп 500,000 →
 *    НӨАТ 50,000, апп 550,000 төлнө. Бэлэн хэсэгт нэмэгдэхгүй.
 *
 * ⚠️ Зээлийн аппын +7% ЦУЦЛАГДСАН (2026-09-28, эзэмшигчийн шийдвэр).
 *    Зээлийн аппаар төлсөн ч нэмэлт дүн бодогдохгүй. `orders.credit_fee_mnt`
 *    багана нь өмнө нь 7%-тай зарагдсан захиалгын ХӨЛДСӨН утгыг хадгалсаар
 *    (баримт, тайлан тэр дүнг харуулна), шинэ захиалгад үргэлж 0.
 *
 * ЗАРЧИМ (эзэмшигчийн шийдвэр)
 *  · Суурь = хөнгөлөлт хассан дүн.
 *  · Нэмэгдэл ОРЛОГО БИШ: захиалгын төлөх дүн, төлбөрт орно, гэхдээ
 *    борлуулалтын тайлан, өрсөлдөөн, урамшуулал `order_lines`-аас
 *    уншдаг тул нэмэгдлийг харахгүй.
 *  · Буцаалтад нэмэгдэл БУЦАХГҮЙ — зөвхөн гутлын дүн.
 *
 * ⚠️ Сервер (`order.confirm`), вэбийн касс, гар утасны POS ГУРВУУЛАА
 *    энэ функцийг дуудна. Клиент өөрөө бодвол төлбөрийн хуваалт 1₮-өөр
 *    зөрж захиалга бүхэлдээ татгалзагдана.
 *
 * ⚠️ «И-баримт» нь энд ЗӨВХӨН 10%-ийн нэмэгдэл. Татварын системд
 *    (PosAPI) баримт илгээхгүй — `orders.ebarimt_*` NULL хэвээр.
 */
import { Decimal } from 'decimal.js';
import { CREDIT_APP_METHOD } from './payment.js';
/** Хувь — бизнесийн шийдвэр. Өөрчлөхөд тестийг хамт зас. */
export const VAT_PCT = 10;
/**
 * 7%-тай зарагдсан ХУУЧИН захиалгын `credit_fee_mnt`-ийн шошго. Баримт,
 * тайлан тэр хөлдсөн дүнг харуулсаар — эс тэгвээс мөрүүд + НӨАТ ≠ НИЙТ.
 * Шинэ захиалгад дүн 0 тул мөр гарахгүй.
 */
export const LEGACY_CREDIT_FEE_LABEL = 'Зээлийн нэмэгдэл';
/** Төгрөг аравтын оронгүй — бүхэл рүү, .5 дээшээ. */
const pctOf = (base, pct) => base.times(pct).dividedBy(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
/**
 * @param baseMnt хөнгөлөлт хассан дүн (гутлын мөрүүдийн нийлбэр − захиалгын хөнгөлөлт)
 */
export function surchargeFor(baseMnt, flags) {
    const base = new Decimal(baseMnt);
    const vatBase = flags.creditBaseMnt ?? (flags.vat ? base : null);
    const vatMnt = vatBase ? pctOf(vatBase, VAT_PCT) : new Decimal(0);
    return { vatMnt, payableMnt: base.plus(vatMnt) };
}
/**
 * Үнээс зээлийн аппаар төлөх хэсэг — и-баримтын суурь. Зээлийн апп
 * байхгүй бол `null`.
 *
 * Бусад хэлбэрийн дүнд нэмэгдэл ОРДОГГҮЙ тул апп = суурь − бусдын
 * нийлбэр. Сервер `payments`-аас, касс үнийн хэсгүүдээс (`chargeParts`)
 * ИЖИЛ функцээр бодно — эс тэгвээс нийлбэр 1₮-өөр зөрнө.
 */
export function creditAppBase(baseMnt, parts) {
    if (!parts.some((p) => p.method === CREDIT_APP_METHOD))
        return null;
    const other = parts
        .filter((p) => p.method !== CREDIT_APP_METHOD)
        .reduce((a, p) => a.plus(new Decimal(p.amountMnt || 0)), new Decimal(0));
    return Decimal.max(0, new Decimal(baseMnt).minus(other));
}
/**
 * Кассын төлбөрийн мөрүүд — ҮНИЙН хэсгүүдээс (нийлбэр = суурь).
 *
 * Кассчин «үнээс энэ хэлбэрээр хэд» гэж оруулна; нэмэгдэл нь сүүлчийн
 * зээлийн аппын мөрд, апп байхгүй бол сүүлчийн мөрд нэмэгдэнэ. Мөрүүдийн
 * нийлбэр ҮРГЭЛЖ `payableMnt` — сервер ижил дүнг гаргана.
 */
export function chargeParts(baseMnt, portions, vat) {
    const surcharge = surchargeFor(baseMnt, { vat, creditBaseMnt: creditAppBase(baseMnt, portions) });
    const credit = portions.map((p) => p.method).lastIndexOf(CREDIT_APP_METHOD);
    const carrier = credit >= 0 ? credit : portions.length - 1;
    const parts = portions.map((p, i) => ({
        ...p,
        amountMnt: new Decimal(p.amountMnt || 0).plus(i === carrier ? surcharge.vatMnt : 0),
    }));
    return { ...surcharge, parts };
}
//# sourceMappingURL=surcharge.js.map